import { useLayoutEffect, useState } from 'react';

import createMentionPlugin from '@draft-js-plugins/mention';
import type { MentionData } from '@draft-js-plugins/mention';
import type { SubMentionComponentProps } from '@draft-js-plugins/mention/lib/Mention';
import type { EntryComponentProps } from '@draft-js-plugins/mention/lib/MentionSuggestions/Entry/Entry';
import styled from 'styled-components';

import { theme } from '@styles/theme';

import { useTextEditorContext } from './text-editor';

import { Avatar } from '../avatar';
import { Stack } from '../stack';
import { Text } from '../text';

type GetUserFn = (id: number) => { id: number; picture: string; roleDisplay: string; name: string };

export function TextEditorPluginMention({
  editMode = false,
  suggestions = [],
  getUser,
}: {
  editMode?: boolean;
  suggestions?: MentionData[];
  getUser?: GetUserFn;
}) {
  const { registerPlugin, readOnly } = useTextEditorContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [plugin] = useState(() =>
    createMentionPlugin({
      theme: {
        mentionSuggestions: 'mention-suggestions-list',
      },
      entityMutability: 'IMMUTABLE',
      mentionComponent: (props) => <MentionText getUser={getUser} editMode={editMode} {...props} />,
    })
  );

  useLayoutEffect(() => {
    registerPlugin('mention', plugin);
  }, [registerPlugin, plugin]);

  if (readOnly) return null;

  const filteredSuggestions = suggestions.filter((user) =>
    user.name.toLowerCase().includes(search)
  );

  return (
    <MentionSuggestionsWrapper>
      <plugin.MentionSuggestions
        open={open}
        onOpenChange={setOpen}
        onSearchChange={({ value }) => setSearch(value.toLowerCase())}
        suggestions={filteredSuggestions}
        entryComponent={MentionEntry}
      />
    </MentionSuggestionsWrapper>
  );
}

export const MentionEntry = ({ mention, isFocused, searchValue, ...rest }: EntryComponentProps) => {
  return (
    <MentionEntryContainer data-focused={isFocused} {...rest}>
      <Stack orientation="horizontal" space={2} align="center">
        <Avatar name={mention.name} src={mention.picture} color="purple" />
        <div>
          <Text font="semibold14" color="purple100" text={mention.name} />
          <Text font="regular14" color="purple80" text={mention.roleDisplay} />
        </div>
      </Stack>
    </MentionEntryContainer>
  );
};

interface MentionTextProps extends SubMentionComponentProps {
  getUser?: GetUserFn;
  editMode: boolean;
}
function MentionText(props: MentionTextProps) {
  const { mention, className, children, getUser, editMode } = props;
  const [open, setOpen] = useState(false);
  const user = getUser ? getUser(Number(mention.id)) : mention;

  if (!user) return null;

  function onMouseEnter() {
    if (editMode) return;
    setOpen(true);
  }

  function onMouseLeave() {
    if (editMode) return;
    setOpen(false);
  }

  return (
    <MentionTextContainer onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <span className={className}>{children}</span>
      <UserInfo data-open={open} aria-hidden>
        <Stack orientation="horizontal" align="center" space={2}>
          <Avatar size="md" name={user.name} src={user.picture} color="purple" />
          <div>
            <Text font="semibold14" color="white100" text={user.name} />
            <Text font="regular14" color="purple40" text={user.roleDisplay} />
          </div>
        </Stack>
      </UserInfo>
    </MentionTextContainer>
  );
}

const MentionTextContainer = styled.span`
  position: relative;
  display: inline-block;
  background: ${theme.color.blue20};
  color: ${theme.color.elementBlue100};
  padding: 0 0.5rem;
  border-radius: 0.25rem;
`;

const UserInfo = styled.div`
  position: absolute;
  white-space: nowrap;
  z-index: 10;
  top: 1.75rem;
  left: 0;
  display: none;
  color: ${theme.color.white100};
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  box-shadow: ${theme.shadow.low};
  background: ${theme.color.purple100};

  ::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    margin-top: -0.5rem;
    margin-left: -0.25rem;
    border-width: 0.25rem;
    border-style: solid;
    border-color: transparent transparent #3d4066 transparent;
  }

  &[data-open='true'] {
    display: block;
  }
`;

const MentionEntryContainer = styled.div`
  padding: 0.5rem;

  &[data-focused='true'] {
    background: ${theme.color.purpleTrans5};
  }
`;

const MentionSuggestionsWrapper = styled.div`
  .mention-suggestions-list {
    margin-top: 0.4em;
    position: absolute;
    min-width: 220px;
    max-width: 440px;
    background: ${theme.color.white100};
    border-radius: 4px;
    box-shadow: ${theme.shadow.medium};
    cursor: pointer;
    padding: 1rem;
    z-index: 10;
    display: flex;
    flex-direction: column;
    transform: scale(0);
    max-height: 300px;
    overflow-y: auto;
  }
`;
